import { useState, useCallback } from 'react'
import { checkProxies, measureProxySpeed, ProxyCheckResult, ProxySpeedResult } from '../api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Loader2, Search, Trash2, AlertCircle, CheckCircle2, XCircle,
  Clock, ChevronDown, ChevronUp, Copy, Shield, Cpu, Fingerprint,
  Globe, Wifi, Eye, Server, Gauge,
} from 'lucide-react'

const MAX_PROXIES = 10

const PROTO_OPTIONS = [
  { value: 'auto', label: 'Авто' },
  { value: 'http', label: 'HTTP' },
  { value: 'https', label: 'HTTPS' },
  { value: 'socks5', label: 'SOCKS5' },
  { value: 'socks4', label: 'SOCKS4' },
]

function maskProxy(raw: string): string {
  const atIdx = raw.indexOf('@')
  if (atIdx === -1) {
    const parts = raw.split(':')
    if (parts.length >= 4) return `${parts[0]}:${parts[1]}:***:***`
    return raw
  }
  const hostPart = raw.slice(atIdx + 1)
  return `***:***@${hostPart}`
}

const VERDICT_STYLES: Record<string, { bg: string; text: string }> = {
  EXCELLENT: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
  GOOD: { bg: 'bg-green-500/15', text: 'text-green-400' },
  MODERATE: { bg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  POOR: { bg: 'bg-orange-500/15', text: 'text-orange-400' },
  BAD: { bg: 'bg-red-500/15', text: 'text-red-400' },
  UNUSABLE: { bg: 'bg-red-500/20', text: 'text-red-500' },
}

function scoreColor(s: number): string {
  if (s >= 80) return 'text-emerald-400'
  if (s >= 60) return 'text-green-400'
  if (s >= 40) return 'text-yellow-400'
  if (s >= 20) return 'text-orange-400'
  return 'text-red-400'
}

function scoreBg(s: number): string {
  if (s >= 80) return 'bg-emerald-500'
  if (s >= 60) return 'bg-green-500'
  if (s >= 40) return 'bg-yellow-500'
  if (s >= 20) return 'bg-orange-500'
  return 'bg-red-500'
}

function latencyColor(ms?: number): string {
  if (!ms) return 'text-muted-foreground'
  if (ms < 500) return 'text-emerald-400'
  if (ms < 1500) return 'text-yellow-400'
  return 'text-red-400'
}

function speedColor(m?: number): string {
  if (m == null) return 'text-muted-foreground'
  if (m >= 50) return 'text-emerald-400'
  if (m >= 15) return 'text-green-400'
  if (m >= 5) return 'text-yellow-400'
  return 'text-orange-400'
}

const ML_LABELS: Record<string, string> = {
  real_mobile: 'Моб. устройство',
  real_desktop: 'Десктоп',
  linux_server: 'Linux сервер',
  vpn_tunnel: 'VPN туннель',
  proxy_bot: 'Прокси / Бот',
}

const ML_COLORS: Record<string, string> = {
  real_mobile: 'bg-emerald-500',
  real_desktop: 'bg-blue-500',
  linux_server: 'bg-orange-500',
  vpn_tunnel: 'bg-yellow-500',
  proxy_bot: 'bg-red-500',
}

const ANON_STYLES: Record<string, { label: string; cls: string }> = {
  elite: { label: 'Elite', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  anonymous: { label: 'Anonymous', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  transparent: { label: 'Transparent', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
}

const SCORE_LABELS: Record<string, string> = {
  anonymity: 'Анонимность',
  fingerprint: 'Fingerprint',
  performance: 'Скорость',
  protocols: 'Протоколы',
  dns: 'DNS',
  geo: 'Гео',
}

export default function ProxyChecker() {
  const [input, setInput] = useState('')
  const [proto, setProto] = useState('auto')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<ProxyCheckResult[]>([])
  const [error, setError] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)

  const lines = input.split('\n').map(l => l.trim()).filter(Boolean)
  const lineCount = lines.length
  const overLimit = lineCount > MAX_PROXIES

  const handleCheck = useCallback(async () => {
    if (lineCount === 0 || overLimit) return
    setLoading(true)
    setError(null)
    setResults([])
    setChecked(false)
    try {
      const res = await checkProxies(lines, proto)
      setResults(res)
      setChecked(true)
    } catch (e: any) {
      setError(e.message || 'Ошибка при проверке')
    } finally {
      setLoading(false)
    }
  }, [lines, lineCount, overLimit, proto])

  const copyResults = useCallback(() => {
    const text = results
      .map(r => {
        if (r.status !== 'ok') return `${r.proxy_original || r.proxy}\tDEAD\t${r.error_code || ''}`
        const parts = [
          r.proxy_original || r.proxy,
          r.verdict || 'OK',
          `${r.overall_score ?? '-'}/100`,
          r.ip || '',
          [r.country, r.city].filter(Boolean).join(', '),
          r.ml_verdict?.prediction || '',
          r.anonymity || '',
          r.latency_ms != null ? `${r.latency_ms}ms` : '',
        ]
        return parts.join('\t')
      })
      .join('\n')
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        const ta = document.createElement('textarea')
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
      })
    } else {
      const ta = document.createElement('textarea')
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
    }
  }, [results])

  const okCount = results.filter(r => r.status === 'ok').length
  const errCount = results.filter(r => r.status !== 'ok').length

  return (
    <div className="p-6 space-y-5 max-w-[1200px]">
      <div className="space-y-3">
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Список прокси <span className="text-muted-foreground/50">(по одной на строку, до {MAX_PROXIES})</span>
            </label>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={"socks5://user:pass@host:port\nhost:port:user:pass\nhttp://host:port"}
              rows={6}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-mono placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y min-h-[120px]"
              disabled={loading}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className={overLimit ? 'text-destructive font-medium' : ''}>
                {lineCount} / {MAX_PROXIES} прокси
              </span>
              <span>Форматы: proto://user:pass@host:port, host:port:user:pass, host:port</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Протокол</label>
            <div className="flex rounded-lg border border-border bg-background overflow-hidden">
              {PROTO_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setProto(opt.value)}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    proto === opt.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-end gap-2 ml-auto">
            {input && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                onClick={() => { setInput(''); setResults([]); setError(null); setChecked(false) }}
                disabled={loading}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Очистить
              </Button>
            )}
            <Button
              size="sm"
              className="h-8 text-xs gap-1.5 px-4"
              onClick={handleCheck}
              disabled={loading || lineCount === 0 || overLimit}
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Search className="w-3.5 h-3.5" />
              )}
              {loading ? 'Проверка...' : 'Проверить'}
            </Button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-3 text-xs text-muted-foreground">
          Глубокая проверка через pushstat.online — до 30 сек для недоступных прокси
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {checked && results.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold">Результаты</h3>
              <div className="flex items-center gap-2">
                {okCount > 0 && (
                  <Badge variant="outline" className="text-[10px] px-2 py-0 bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                    {okCount} alive
                  </Badge>
                )}
                {errCount > 0 && (
                  <Badge variant="outline" className="text-[10px] px-2 py-0 bg-red-500/10 text-red-400 border-red-500/30">
                    {errCount} dead
                  </Badge>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 text-muted-foreground" onClick={copyResults}>
              <Copy className="w-3 h-3" />
              Копировать
            </Button>
          </div>

          <div className="space-y-2">
            {results.map((r, i) => (
              <ResultCard key={i} result={r} proto={proto} />
            ))}
          </div>
        </div>
      )}

      {checked && results.length === 0 && !error && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          Нет результатов. Проверьте формат введённых прокси.
        </div>
      )}
    </div>
  )
}


function ResultCard({ result: r, proto }: { result: ProxyCheckResult; proto: string }) {
  const [expanded, setExpanded] = useState(false)
  const [speed, setSpeed] = useState<ProxySpeedResult | null>(r.speed || null)
  const [speedBusy, setSpeedBusy] = useState(false)
  const isOk = r.status === 'ok'
  const vs = r.verdict ? VERDICT_STYLES[r.verdict] || VERDICT_STYLES.MODERATE : null

  // Idea #98 — замер скорости в Мбит/с по кнопке (тяжёлое скачивание не в общем чеке).
  const runSpeed = useCallback(async () => {
    setSpeedBusy(true)
    try {
      // Подтверждённый чекером протокол точнее, чем 'auto' из дропдауна —
      // это спасает bare-форму host:port:user:pass (иначе нет схемы для socks/http).
      const p = r.proto_confirmed || proto
      const res = await measureProxySpeed(r.proxy_original || r.proxy, p)
      setSpeed(res)
    } catch (e: any) {
      setSpeed({ checked: true, ok: false, detail: e?.message || 'ошибка' })
    } finally {
      setSpeedBusy(false)
    }
  }, [r.proxy_original, r.proxy, r.proto_confirmed, proto])

  // Server-side reachability: the path Chrome actually uses. A proxy that is
  // green on pushstat but NOT reachable from this server will never work in
  // real campaigns — surface that mismatch loudly.
  const local = r.local
  const localChecked = !!local?.checked
  const localOk = localChecked && local?.reachable === true
  const localBad = localChecked && local?.reachable === false
  const falseGreen = isOk && localBad

  return (
    <div className={`rounded-lg border overflow-hidden transition-colors ${
      falseGreen ? 'border-amber-500/40 bg-amber-500/5'
        : isOk ? 'border-border bg-card' : 'border-red-500/20 bg-red-500/5'
    }`}>
      <button
        onClick={() => isOk && setExpanded(!expanded)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
          isOk ? 'hover:bg-accent/40 cursor-pointer' : 'cursor-default'
        }`}
      >
        {isOk && r.overall_score != null ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold ${vs?.bg || 'bg-muted'} ${vs?.text || 'text-muted-foreground'}`}>
              {r.overall_score}
            </span>
            {r.verdict && (
              <span className={`text-[10px] font-bold uppercase tracking-wider ${vs?.text || 'text-muted-foreground'}`}>
                {r.verdict}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-500/15 text-red-400">
              <XCircle className="w-5 h-5" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-400">DEAD</span>
          </div>
        )}

        <span className="font-mono text-xs text-muted-foreground truncate min-w-0 max-w-[200px]">
          {maskProxy(r.proxy_original || r.proxy)}
        </span>

        {isOk ? (
          <>
            <span className="font-mono text-xs shrink-0 hidden sm:block">
              {r.ip || '\u2014'}
            </span>

            <span className="text-xs text-muted-foreground truncate hidden md:block max-w-[140px]">
              {[r.country, r.city].filter(Boolean).join(', ') || '\u2014'}
            </span>

            {r.ml_verdict && (
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 hidden lg:inline-flex ${
                r.ml_verdict.prediction === 'real_mobile' ? 'text-emerald-400 border-emerald-500/30' :
                r.ml_verdict.prediction === 'real_desktop' ? 'text-blue-400 border-blue-500/30' :
                r.ml_verdict.prediction === 'linux_server' ? 'text-orange-400 border-orange-500/30' :
                'text-red-400 border-red-500/30'
              }`}>
                {ML_LABELS[r.ml_verdict.prediction] || r.ml_verdict.prediction}
              </Badge>
            )}

            {r.anonymity && ANON_STYLES[r.anonymity] && (
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 hidden lg:inline-flex ${ANON_STYLES[r.anonymity].cls}`}>
                {ANON_STYLES[r.anonymity].label}
              </Badge>
            )}

            {r.latency_ms != null && (
              <span className={`inline-flex items-center gap-1 text-xs shrink-0 ${latencyColor(r.latency_ms)}`}>
                <Clock className="w-3 h-3" />
                <span className="font-mono">{r.latency_ms}</span>
                <span className="text-[10px] text-muted-foreground">ms</span>
              </span>
            )}

            {speed?.ok && speed.mbps != null && (
              <span
                title={`Реальная скорость через прокси${speed.partial ? ' (частичный замер)' : ''}`}
                className={`inline-flex items-center gap-1 text-xs shrink-0 ${speedColor(speed.mbps)}`}
              >
                <Gauge className="w-3 h-3" />
                <span className="font-mono">{speed.mbps}</span>
                <span className="text-[10px] text-muted-foreground">Мбит/с</span>
              </span>
            )}

            {localChecked && (
              <Badge
                variant="outline"
                title={local?.detail || ''}
                className={`text-[10px] px-1.5 py-0 shrink-0 hidden md:inline-flex ${
                  localOk
                    ? 'text-emerald-400 border-emerald-500/30'
                    : 'text-amber-400 border-amber-500/40 bg-amber-500/10'
                }`}
              >
                {localOk ? 'C сервера: OK' : 'C сервера: НЕТ'}
              </Badge>
            )}

            <span className="ml-auto shrink-0 text-muted-foreground">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </>
        ) : (
          <span className="text-xs text-red-400/80 truncate">
            {r.error_message || r.error_code || 'Connection failed'}
          </span>
        )}
      </button>

      {falseGreen && (
        <div className="flex items-start gap-2 px-4 py-2.5 border-t border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-300">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Прокси проходит проверку на pushstat, но <b>недоступен с вашего сервера</b>
            {local?.stage ? ` (этап: ${local.stage})` : ''} — браузер им пользоваться
            <b> не сможет</b>. {local?.detail && local.detail !== 'ok' ? `Причина: ${local.detail}. ` : ''}
            Обычно это закрытый firewall, IPv6-only вход прокси (режется kill-switch'ем) или мёртвый/неверный вход.
          </span>
        </div>
      )}

      {expanded && isOk && (
        <div className="border-t border-border px-4 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {r.scores && (
              <DetailSection icon={Shield} title="Оценки по категориям">
                <div className="space-y-2">
                  {(Object.entries(r.scores) as [string, number][]).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-[11px] text-muted-foreground w-24 shrink-0">{SCORE_LABELS[key] || key}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full rounded-full ${scoreBg(val)} transition-all`} style={{ width: `${val}%` }} />
                      </div>
                      <span className={`text-[11px] font-mono font-bold w-7 text-right ${scoreColor(val)}`}>{val}</span>
                    </div>
                  ))}
                </div>
              </DetailSection>
            )}

            {r.ml_verdict && (
              <DetailSection icon={Cpu} title="ML-анализ">
                <div className="space-y-2">
                  {Object.entries(r.ml_verdict.probabilities)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cls, prob]) => (
                      <div key={cls} className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground w-28 shrink-0 truncate">
                          {ML_LABELS[cls] || cls}
                        </span>
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full ${ML_COLORS[cls] || 'bg-zinc-500'} transition-all`}
                            style={{ width: `${Math.max(prob * 100, 1)}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-mono w-10 text-right text-muted-foreground">
                          {(prob * 100).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                </div>
              </DetailSection>
            )}

            {r.fingerprint && (
              <DetailSection icon={Fingerprint} title="TLS-отпечаток">
                <div className="space-y-1.5 text-[11px]">
                  <InfoRow label="JA3" value={r.fingerprint.ja3} mono truncate />
                  <InfoRow label="JA4" value={r.fingerprint.ja4} mono truncate />
                  {r.fingerprint.ja3_match && <InfoRow label="Совпадение" value={r.fingerprint.ja3_match} />}
                  <InfoRow label="TLS" value={r.fingerprint.tls_version} />
                  <InfoRow label="Браузер" value={r.fingerprint.browser_guess || '\u2014'} />
                  <InfoRow label="GREASE" value={r.fingerprint.has_grease ? 'Да' : 'Нет'} ok={r.fingerprint.has_grease} />
                  <InfoRow label="uTLS" value={r.fingerprint.utls_suspected ? 'Подозрение' : 'Нет'} ok={!r.fingerprint.utls_suspected} />
                  <InfoRow label="Шифры" value={`${r.fingerprint.cipher_count}`} />
                  <InfoRow label="Расширения" value={`${r.fingerprint.extension_count}`} />
                </div>
              </DetailSection>
            )}

            {r.tcp && (
              <DetailSection icon={Wifi} title="TCP-стек">
                <div className="space-y-1.5 text-[11px]">
                  <InfoRow label="ОС" value={r.tcp.os_detected} />
                  <InfoRow label="wscale" value={`${r.tcp.wscale}`} mono />
                  <InfoRow label="Uptime" value={`${r.tcp.uptime_days} дн.`} />
                  <InfoRow label="MSS" value={`${r.tcp.mss}`} mono />
                  <InfoRow label="MTU" value={`${r.tcp.mtu}`} mono />
                  <InfoRow label="TTL" value={`${r.tcp.initial_ttl}`} mono />
                  <InfoRow label="Хопы" value={`${r.tcp.hop_distance}`} mono />
                  <InfoRow label="VPN-тунн." value={r.tcp.vpn_tunnel ? 'Да' : 'Нет'} ok={!r.tcp.vpn_tunnel} />
                  <InfoRow label="TCP opts" value={r.tcp.options_order.join(', ')} mono truncate />
                </div>
              </DetailSection>
            )}

            {r.protocols && (
              <DetailSection icon={Globe} title="Протоколы">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                  <ProtoFlag label="HTTPS" ok={r.protocols.https} />
                  <ProtoFlag label="HTTP" ok={r.protocols.http} />
                  <ProtoFlag label="IPv4" ok={r.protocols.ipv4} />
                  <ProtoFlag label="IPv6" ok={r.protocols.ipv6} />
                </div>
                {r.proto_confirmed && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Подтверждённый: <span className="font-mono font-medium text-foreground">{r.proto_confirmed}</span>
                  </div>
                )}
              </DetailSection>
            )}

            <DetailSection icon={Eye} title="Репутация">
              <div className="space-y-1.5 text-[11px]">
                <InfoRow label="Proxy det." value={r.proxy_detected ? 'Да' : 'Нет'} ok={!r.proxy_detected} />
                <InfoRow label="VPN det." value={r.vpn_detected ? 'Да' : 'Нет'} ok={!r.vpn_detected} />
                <InfoRow label="Хостинг" value={r.hosting ? 'Datacenter' : 'Residential'} ok={!r.hosting} />
                <InfoRow label="Чёрный сп." value={r.blacklisted ? 'Да' : 'Нет'} ok={!r.blacklisted} />
                <InfoRow label="DNS leak" value={r.dns_leak ? 'Да' : 'Нет'} ok={!r.dns_leak} />
                {r.risk_score != null && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-20 shrink-0">Risk score</span>
                    <span className={`font-mono font-bold ${
                      r.risk_score <= 10 ? 'text-emerald-400' :
                      r.risk_score <= 30 ? 'text-yellow-400' :
                      r.risk_score <= 60 ? 'text-orange-400' : 'text-red-400'
                    }`}>{r.risk_score}/100</span>
                  </div>
                )}
                {r.blacklist_sources && r.blacklist_sources.length > 0 && (
                  <div className="mt-1 text-red-400 text-[10px]">
                    Причины: {r.blacklist_sources.join(', ')}
                  </div>
                )}
              </div>
            </DetailSection>
          </div>

          <div className="pt-3 border-t border-border">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={runSpeed}
                disabled={speedBusy}
              >
                {speedBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gauge className="w-3.5 h-3.5" />}
                {speedBusy ? 'Замеряю…' : 'Замерить скорость'}
              </Button>
              {speed && (
                speed.ok && speed.mbps != null ? (
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className={`inline-flex items-baseline gap-1 font-mono font-bold ${speedColor(speed.mbps)}`}>
                      <span className="text-base">{speed.mbps}</span>
                      <span className="text-[10px] text-muted-foreground">Мбит/с</span>
                    </span>
                    {speed.ttfb_ms != null && (
                      <span className="text-muted-foreground">TTFB: <span className="font-mono text-foreground">{speed.ttfb_ms}ms</span></span>
                    )}
                    {speed.bytes != null && (
                      <span className="text-muted-foreground">Скачано: <span className="font-mono text-foreground">{(speed.bytes / 1e6).toFixed(1)}MB</span></span>
                    )}
                    {speed.partial && <span className="text-amber-400">частичный</span>}
                  </div>
                ) : (
                  <span className="text-[11px] text-red-400">
                    {speed.detail || 'не удалось замерить'}
                  </span>
                )
              )}
              {!speed && !speedBusy && (
                <span className="text-[11px] text-muted-foreground">
                  Реальная пропускная способность через этот прокси (важно при VPN)
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2 border-t border-border text-[11px] text-muted-foreground">
            {r.ip && (
              <span className="flex items-center gap-1">
                <Server className="w-3 h-3" />
                {r.ip}
                {r.ip_version && <span className="text-[10px]">(IPv{r.ip_version})</span>}
              </span>
            )}
            {r.isp && <span>ISP: {r.isp}</span>}
            {r.asn && <span className="font-mono">{r.asn}</span>}
            {r.elapsed_sec != null && <span className="ml-auto">Проверка: {r.elapsed_sec.toFixed(1)}с</span>}
          </div>
        </div>
      )}
    </div>
  )
}


function DetailSection({ icon: Icon, title, children }: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      {children}
    </div>
  )
}


function InfoRow({ label, value, mono, truncate, ok }: {
  label: string
  value: string
  mono?: boolean
  truncate?: boolean
  ok?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground w-20 shrink-0">{label}</span>
      <span className={[
        mono ? 'font-mono' : '',
        truncate ? 'truncate max-w-[160px]' : '',
        ok === true ? 'text-emerald-400' : ok === false ? 'text-red-400' : 'text-foreground',
      ].filter(Boolean).join(' ')}>
        {value}
      </span>
    </div>
  )
}


function ProtoFlag({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {ok ? (
        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
      ) : (
        <XCircle className="w-3 h-3 text-red-400/60" />
      )}
      <span className={ok ? 'text-foreground' : 'text-muted-foreground/60'}>{label}</span>
    </div>
  )
}
